import React, { useMemo, useState } from 'react';
import { Box, Chip, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ApprovalIcon from '@mui/icons-material/Approval';
import BedIcon from '@mui/icons-material/Bed';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import HandymanIcon from '@mui/icons-material/Handyman';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import InventoryIcon from '@mui/icons-material/Inventory';
import MaleIcon from '@mui/icons-material/Male';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicationIcon from '@mui/icons-material/MedicalServices';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PeopleIcon from '@mui/icons-material/People';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import ScienceIcon from '@mui/icons-material/Science';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { Horse } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { hasPagePermission, isSystemAdminSession } from '../../utils/accessControl';

const COPY = {
  ar: {
    title: 'لوحة التحكم',
    subtitle: 'إدارة القوائم المرجعية، الصلاحيات، الخدمات الطبية، وإعدادات التشغيل.',
    search: 'بحث في الإعدادات',
    all: 'الكل',
    operations: 'التشغيل',
    medical: 'العيادة والإنتاج',
    security: 'الصلاحيات والمستخدمون',
    system: 'إعدادات عامة',
    backup: 'النسخ الاحتياطي',
    open: 'فتح',
    noResults: 'لا توجد عناصر مطابقة',
    labels: {
      clinicApprovals: 'اعتمادات العيادة',
      stable: 'الإسطبلات',
      rooms: 'الغرف',
      colors: 'الألوان',
      breeds: 'السلالات',
      accessControl: 'مركز الصلاحيات',
      auditReport: 'تقرير التدقيق',
      accommodationReport: 'تقرير التسكين',
      systemHealth: 'صحة النظام',
      actorIntegrity: 'سلامة المنفّذ',
      systemUpdate: 'تحديث النظام',
      databaseBackup: 'النسخ الاحتياطي',
      websiteName: 'الإعدادات العامة',
      pricing: 'إدارة الأسعار',
      procedures: 'إجراءات العيادة',
      medications: 'الأدوية',
      farrierMaterials: 'مواد البيطار',
      farrierProcedures: 'إجراءات البيطار',
      stallionManagement: 'إدارة الأفحل',
      breedingProcedures: 'إجراءات الإنتاج',
      semenInventory: 'مخزون السائل المنوي',
      owners: 'الملاك',
      users: 'المستخدمون',
      vetCheckupReport: 'تقرير الفحص البيطري',
      horseActivation: 'تفعيل الخيل',
      checkupTypes: 'أنواع الفحص',
      notificationsTemplates: 'قوالب التنبيهات',
    },
  },
  en: {
    title: 'Control Panel',
    subtitle: 'Manage reference lists, permissions, medical services, and operational setup.',
    search: 'Search settings',
    all: 'All',
    operations: 'Operations',
    medical: 'Clinic & Breeding',
    security: 'Permissions & Users',
    system: 'General',
    backup: 'Backups',
    open: 'Open',
    noResults: 'No matching items',
    labels: {
      clinicApprovals: 'Clinic Approvals',
      stable: 'Stable',
      rooms: 'Rooms',
      colors: 'Colors',
      breeds: 'Breeds',
      accessControl: 'Access Control',
      auditReport: 'Audit Report',
      accommodationReport: 'Accommodation Report',
      systemHealth: 'System Health',
      actorIntegrity: 'Actor Integrity',
      systemUpdate: 'System Update',
      databaseBackup: 'Database Backup',
      websiteName: 'General settings',
      pricing: 'Pricing control',
      procedures: 'Procedures',
      medications: 'Medications',
      farrierMaterials: 'Farrier Materials',
      farrierProcedures: 'Farrier Procedures',
      stallionManagement: 'Stallion Management',
      breedingProcedures: 'Breeding Procedures',
      semenInventory: 'Semen Inventory',
      owners: 'Owners',
      users: 'Users',
      vetCheckupReport: 'Vet Checkup Report',
      horseActivation: 'Horse Activation',
      checkupTypes: 'Checkup Types',
      notificationsTemplates: 'Notifications Templates',
    },
  },
};

// مرتّبة حسب الموضوع ثم الأهمية داخل كل موضوع (يحدّد ترتيب العرض في تبويب «الكل»).
const settingsOptions = [
  // 1) إعدادات عامة للنظام — أول موضوع
  { key: 'websiteName', category: 'system', icon: <SettingsIcon />, path: '/SystemSettings' },
  { key: 'pricing', category: 'system', icon: <PriceChangeIcon />, path: '/settings/pricing', page: 'settings/pricing' },
  { key: 'notificationsTemplates', category: 'system', icon: <NotificationsActiveIcon />, path: '/OwnerAlertTemplates', perm: 'OwnerAlertTemplates' },
  { key: 'horseActivation', category: 'system', icon: <RestoreFromTrashIcon />, path: '/RestoreDeletedHorses' },

  // 2) الصلاحيات والمستخدمون
  { key: 'accessControl', category: 'security', icon: <AdminPanelSettingsIcon />, path: '/settings/access-control', page: 'access-control' },
  { key: 'users', category: 'security', icon: <PeopleIcon />, path: '/user-list' },
  { key: 'owners', category: 'security', icon: <SupervisedUserCircleIcon />, path: '/owner-list' },
  { key: 'auditReport', category: 'security', icon: <ManageSearchIcon />, path: '/settings/audit-report', page: 'settings/audit-report' },
  { key: 'systemHealth', category: 'security', icon: <MonitorHeartIcon />, path: '/system-health', page: 'systemhealth' },
  { key: 'actorIntegrity', category: 'security', icon: <ManageSearchIcon />, path: '/actor-integrity', page: 'actorintegrity' },
  { key: 'systemUpdate', category: 'security', icon: <SystemUpdateAltIcon />, path: '/system-update', page: 'systemupgrade' },

  // 3) التشغيل — قوائم مرجعية وبُنى الإسطبل
  { key: 'stable', category: 'operations', icon: <HomeWorkIcon />, path: '/stable-list' },
  { key: 'rooms', category: 'operations', icon: <MeetingRoomIcon />, path: '/stable-room-list' },
  { key: 'accommodationReport', category: 'operations', icon: <BedIcon />, path: '/settings/accommodation-report', page: 'settings/accommodation-report' },
  { key: 'breeds', category: 'operations', icon: <Horse size={24} weight="fill" />, path: '/horse-breeds' },
  { key: 'colors', category: 'operations', icon: <ColorLensIcon />, path: '/horse-colors' },

  // 4) العيادة والإنتاج — خدمات طبية وكتالوجات
  { key: 'clinicApprovals', category: 'medical', icon: <ApprovalIcon />, path: '/ClinicApprovals' },
  { key: 'vetCheckupReport', category: 'medical', icon: <HealthAndSafetyIcon />, path: '/VetCheckupReport', page: 'VetCheckupReport' },
  { key: 'checkupTypes', category: 'medical', icon: <MedicalServicesIcon />, path: '/CheckupTypesPage' },
  { key: 'procedures', category: 'medical', icon: <HandymanIcon />, path: '/procedures' },
  { key: 'medications', category: 'medical', icon: <MedicationIcon />, path: '/medications' },
  { key: 'farrierProcedures', category: 'medical', icon: <HandymanIcon />, path: '/farrier-procedures' },
  { key: 'farrierMaterials', category: 'medical', icon: <InventoryIcon />, path: '/farrier-materials' },
  { key: 'stallionManagement', category: 'medical', icon: <MaleIcon />, path: '/StallionForm', page: 'StallionForm' },
  { key: 'breedingProcedures', category: 'medical', icon: <ScienceIcon />, path: '/ReproductionProceduresList' },
  { key: 'semenInventory', category: 'medical', icon: <InventoryIcon />, path: '/semen-inventory', page: 'semen-inventory' },

  // 5) النسخ الاحتياطي — أداة صيانة
  { key: 'databaseBackup', category: 'backup', icon: <StorageIcon />, path: '/settings/backups', page: 'settings/backups' },
];

const categoryColor = {
  operations: { color: COLORS.gold, soft: COLORS.goldSoft },
  medical: { color: COLORS.green, soft: COLORS.greenSoft },
  security: { color: COLORS.blue, soft: COLORS.blueSoft },
  system: { color: COLORS.rose, soft: COLORS.roseSoft },
  backup: { color: COLORS.warning, soft: COLORS.warningSoft },
};

const Settings = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const text = COPY[i18n.language === 'ar' ? 'ar' : 'en'];
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const permissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('permissions') || '[]');
    } catch (_) {
      return [];
    }
  }, []);

  const categories = [
    { key: 'all', label: text.all },
    { key: 'system', label: text.system },
    { key: 'security', label: text.security },
    { key: 'operations', label: text.operations },
    { key: 'medical', label: text.medical },
    { key: 'backup', label: text.backup },
  ];

  // ترتيب المواضيع حسب الأهمية — يحكم ترتيب الأقسام في العرض.
  const CATEGORY_ORDER = ['system', 'security', 'operations', 'medical', 'backup'];
  const categoryLabel = {
    security: text.security,
    system: text.system,
    operations: text.operations,
    medical: text.medical,
    backup: text.backup,
  };

  const visibleOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return settingsOptions.filter((option) => {
      const categoryMatch = category === 'all' || option.category === category;
      // System Admin يرى كل بطاقات الإعدادات (مثل تجاوز المسار) — يمنع إخفاء صفحة
      // جديدة (مثل systemupgrade) عن الأدمن قبل وجود صفّ صلاحية في القاعدة.
      const permissionMatch = !option.page || isSystemAdminSession() || hasPagePermission(permissions, [option.page], option.path);
      const label = text.labels[option.key] || option.key;
      const queryMatch = !needle || label.toLowerCase().includes(needle) || option.path.toLowerCase().includes(needle);
      return categoryMatch && permissionMatch && queryMatch;
    });
  }, [category, permissions, query, text.labels]);

  // تجميع العناصر الظاهرة تحت مواضيعها بالترتيب، مع تجاهل المواضيع الفارغة.
  const groupedOptions = CATEGORY_ORDER
    .map((key) => ({ key, label: categoryLabel[key], items: visibleOptions.filter((o) => o.category === key) }))
    .filter((group) => group.items.length > 0);

  const renderCard = (option) => {
    const palette = categoryColor[option.category] || categoryColor.system;
    return (
      <Paper
        key={`${option.key}-${option.path}`}
        elevation={0}
        onClick={() => navigate(option.path)}
        sx={{
          ...cardSx,
          p: 2,
          cursor: 'pointer',
          minHeight: 96,
          transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: palette.color,
            boxShadow: '0 12px 28px rgba(91, 75, 39, 0.10)',
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            sx={{
              width: 50,
              height: 50,
              bgcolor: palette.soft,
              color: palette.color,
              '&:hover': { bgcolor: palette.soft },
            }}
          >
            {option.icon}
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: COLORS.text, fontWeight: 900, lineHeight: 1.2 }}>{text.labels[option.key] || option.key}</Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box
      className="smart-page-container"
      sx={{
        minHeight: '100vh',
        bgcolor: COLORS.page,
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{text.subtitle}</Typography>
          </Box>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={text.search}
            size="small"
            sx={{ width: { xs: '100%', md: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        {categories.map((item) => (
          <Chip
            key={item.key}
            label={item.label}
            onClick={() => setCategory(item.key)}
            sx={{
              bgcolor: category === item.key ? COLORS.gold : COLORS.card,
              color: category === item.key ? '#fff' : COLORS.text,
              border: `1px solid ${category === item.key ? COLORS.gold : COLORS.line}`,
              fontWeight: 800,
            }}
          />
        ))}
      </Stack>

      <Stack spacing={3.5} sx={{ width: '100%' }}>
        {groupedOptions.map((group) => {
          const palette = categoryColor[group.key] || categoryColor.system;
          return (
            <Box key={group.key}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                <Box sx={{ width: 6, height: 22, borderRadius: 1, bgcolor: palette.color }} />
                <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{group.label}</Typography>
                <Chip
                  size="small"
                  label={group.items.length}
                  sx={{ bgcolor: palette.soft, color: palette.color, fontWeight: 800, height: 22 }}
                />
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 2,
                  width: '100%',
                }}
              >
                {group.items.map((option) => renderCard(option))}
              </Box>
            </Box>
          );
        })}
        {groupedOptions.length === 0 && (
          <Typography sx={{ color: COLORS.muted, textAlign: 'center', py: 4 }}>{text.noResults}</Typography>
        )}
      </Stack>
    </Box>
  );
};

export default Settings;
